/**
 * Elección del foco del Día 5.
 *
 * NUNCA es una caja negra. Muestra las razones concretas en español llano —
 * construidas a partir del cálculo real, no de prosa inventada— y el usuario
 * siempre puede elegir otra cosa.
 *
 * Y si no hay datos suficientes, lo DICE. Inventar una recomendación sin
 * señales sería peor que no dar ninguna: parecería fundada y no lo estaría.
 */

import { AREAS, NOMBRE_AREA, type Area } from '../../domain/modelo/areas';
import {
  MENSAJE_SIN_DATOS,
  type Recomendacion,
} from '../../domain/reglas/puntuacionDebilidad';
import estilos from './FocoDia5.module.css';

interface Props {
  recomendacion: Recomendacion;
  /** Foco ya fijado en la sesión, si existe. */
  focoElegido: Area | null;
  onElegir: (area: Area) => void;
}

export function FocoDia5({ recomendacion, focoElegido, onElegir }: Props) {
  const sugerida = recomendacion.tipo === 'sugerencia' ? recomendacion.mejor : null;
  const esManual = Boolean(focoElegido && sugerida && focoElegido !== sugerida.area);

  return (
    <section className={estilos.bloque} aria-label="Foco del Día 5">
      {sugerida ? (
        <>
          <p className={estilos.etiqueta}>Foco sugerido para hoy</p>
          <h3 className={estilos.area}>{NOMBRE_AREA[sugerida.area]}</h3>

          <p className={estilos.porQue}>¿Por qué?</p>
          <ul className={estilos.razones}>
            {sugerida.razones.map((razon) => (
              <li key={razon.texto}>{razon.texto}</li>
            ))}
          </ul>

          {focoElegido === null ? (
            <button
              type="button"
              className={estilos.principal}
              onClick={() => onElegir(sugerida.area)}
            >
              Usar este foco
            </button>
          ) : null}
        </>
      ) : (
        <>
          <p className={estilos.sinDatos}>{MENSAJE_SIN_DATOS}</p>
          <p className={estilos.explicacion}>
            Pasa cuando la semana todavía no dejó señales: pocas sesiones cerradas, sin
            autoevaluaciones o sin dificultades registradas. Elegí vos qué querés
            trabajar hoy — es preferible a una recomendación inventada.
          </p>
        </>
      )}

      <div className={estilos.opciones}>
        <p className={estilos.etiqueta}>
          {focoElegido ? 'Tu foco de hoy' : sugerida ? 'O elegí otro' : 'Elegí un foco'}
        </p>
        <div className={estilos.chips} role="group" aria-label="Elegir foco">
          {AREAS.map((area) => (
            <button
              key={area}
              type="button"
              className={
                focoElegido === area ? `${estilos.chip} ${estilos.chipActivo}` : estilos.chip
              }
              aria-pressed={focoElegido === area}
              onClick={() => onElegir(area)}
            >
              {NOMBRE_AREA[area]}
            </button>
          ))}
        </div>
      </div>

      {esManual ? (
        <p className={estilos.override} role="status">
          Elegiste un foco distinto del sugerido. Queda registrado así: sirve para saber
          si la recomendación te está ayudando de verdad.
        </p>
      ) : null}

      {focoElegido ? (
        <p className={estilos.fijado}>
          El foco de hoy queda fijado en <strong>{NOMBRE_AREA[focoElegido]}</strong>. No
          va a cambiar durante la sesión aunque cambien tus datos.
        </p>
      ) : null}
    </section>
  );
}
